import { useState, useEffect } from 'react'
import { RouteData, POI } from '@cya-routes/shared'
import { Comment, CommentCategory } from '../../types/comments'
import { useRouteComments } from '../../hooks/useRouteComments'
import { useAppStore } from '@cya-routes/shared'
import { MessageSquare, MapPin, Plus, AlertTriangle, CloudSun, Lightbulb, HelpCircle, MessageCircle, ChevronDown, ChevronRight, Construction, CheckCircle, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { AddCommentModal } from '../comments/AddCommentModal'
import { CommentDetailModal } from '../comments/CommentDetailModal'
import { UserPOI } from '../../types/userPoi'
import { getUserPOICommentCounts } from '../../services/firebase/userPoiComments'
import { getDistanceAlongRoute } from '../../utils/routeDistanceCalculator'

interface DiscussionTabProps {
  routeId: string
  routes: RouteData[]
  selectedRouteId: string
  getCurrentRoute: () => RouteData | null
  userPOIs?: UserPOI[]
  showUserPOIs?: boolean
  onUserPOIClick?: (poi: UserPOI) => void
  showResolved?: boolean
  onToggleResolved?: () => void
}

const getCategoryIcon = (category: CommentCategory) => {
  switch (category) {
    case 'hazard': return <AlertTriangle className="w-4 h-4" />
    case 'condition': return <CloudSun className="w-4 h-4" />
    case 'tip': return <Lightbulb className="w-4 h-4" />
    case 'question': return <HelpCircle className="w-4 h-4" />
    case 'discussion': return <MessageCircle className="w-4 h-4" />
  }
}

const getCategoryColor = (category: CommentCategory) => {
  switch (category) {
    case 'hazard': return 'text-red-400 bg-red-500/10 border-red-500/20'
    case 'condition': return 'text-blue-400 bg-blue-500/10 border-blue-500/20'
    case 'tip': return 'text-green-400 bg-green-500/10 border-green-500/20'
    case 'question': return 'text-purple-400 bg-purple-500/10 border-purple-500/20'
    case 'discussion': return 'text-gray-400 bg-gray-500/10 border-gray-500/20'
  }
}

export function DiscussionTab({
  routeId,
  routes,
  selectedRouteId,
  getCurrentRoute,
  userPOIs: propUserPOIs,
  showUserPOIs = true,
  onUserPOIClick,
  showResolved = false,
  onToggleResolved
}: DiscussionTabProps) {
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(false)
  const [expandedSection, setExpandedSection] = useState<'discussion' | 'reports' | null>('discussion')
  const [showAddModal, setShowAddModal] = useState(false)
  const [addModalType, setAddModalType] = useState<'pinned' | 'general'>('general')
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [poiCommentCounts, setPoiCommentCounts] = useState<Record<string, number>>({})
  
  const { auth } = useAppStore()
  const user = auth.currentUser
  const currentRoute = routes.find(r => r.id === selectedRouteId) || getCurrentRoute()
  
  // Construct display name from user object
  const userDisplayName = user ? `${user.firstName} ${user.lastName}`.trim() : undefined

  const {
    comments,
    pinnedComments,
    generalComments,
    loading,
    error,
    addComment,
    editComment,
    removeComment,
    confirmCommentAccuracy,
    disputeCommentAccuracy,
    toggleCommentHelpful,
    refreshComments
  } = useRouteComments({
    routeId,
    route: currentRoute,
    userId: user?.id,
    userDisplayName: userDisplayName,
    userPhotoURL: user?.profilePhoto
  })

  // Use user POIs from props
  const userPOIs = propUserPOIs || []
  const poisLoading = false

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsTabletOrSmaller(window.innerWidth < 1024)
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Fetch comment counts for user POIs
  useEffect(() => {
    const fetchCommentCounts = async () => {
      if (userPOIs && userPOIs.length > 0) {
        try {
          const poiIds = userPOIs.map(poi => poi.id)
          const counts = await getUserPOICommentCounts(poiIds)
          setPoiCommentCounts(counts)
        } catch (error) {
          console.error('Failed to fetch comment counts:', error)
        }
      }
    }
    
    fetchCommentCounts()
  }, [userPOIs])

  const handleAddComment = async (data: any) => {
    try {
      await addComment(data)
      setShowAddModal(false)
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  const handleCommentClick = (comment: Comment) => {
    setSelectedComment(comment)
    setShowDetailModal(true)
  }

  const handleEditComment = async (commentId: string, text: string) => {
    try {
      await editComment(commentId, { text })
      setShowDetailModal(false)
      await refreshComments()
    } catch (error) {
      console.error('Failed to edit comment:', error)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    try {
      await removeComment(commentId)
      setShowDetailModal(false)
    } catch (error) {
      console.error('Failed to delete comment:', error)
    }
  }

  const handleResolveComment = async (commentId: string, resolved: boolean) => {
    try {
      await editComment(commentId, { resolved })
      if (resolved) {
        setShowDetailModal(false)
      }
      await refreshComments()
    } catch (error) {
      console.error('Failed to update comment:', error)
    }
  }

  // Component for rendering a single comment
  const CommentItem = ({ comment, isPinned = false }: { comment: Comment, isPinned?: boolean }) => (
    <div 
      className={`p-3 rounded-lg border cursor-pointer transition-all hover:bg-white/5 ${
        comment.resolved ? 'opacity-60' : ''
      } ${getCategoryColor(comment.category)}`}
      onClick={() => handleCommentClick(comment)}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          {getCategoryIcon(comment.category)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-sm">{comment.authorName}</span>
            {isPinned && comment.distanceAlongRoute !== undefined && (
              <span className="text-xs text-gray-400">
                @ {(comment.distanceAlongRoute / 1000).toFixed(1)}km
              </span>
            )}
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
            </span>
          </div>
          <p className="text-sm text-gray-200 line-clamp-2">{comment.text}</p>
          {comment.replyCount && comment.replyCount > 0 && (
            <span className="text-xs text-gray-400 mt-1">
              {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}
            </span>
          )}
        </div>
      </div>
    </div>
  )

  // Component for rendering a user POI
  const UserPOIItem = ({ poi }: { poi: UserPOI }) => {
    const getPoiIcon = (type: string) => {
      switch (type) {
        case 'hazard': return <AlertTriangle className="w-4 h-4" />
        case 'condition': return <CloudSun className="w-4 h-4" />
        case 'tip': return <Lightbulb className="w-4 h-4" />
        case 'roadworks': return <Construction className="w-4 h-4" />
        default: return <MapPin className="w-4 h-4" />
      }
    }

    const getPoiColor = (type: string) => {
      switch (type) {
        case 'hazard': return 'text-red-400 bg-red-500/10 border-red-500/20'
        case 'condition': return 'text-orange-400 bg-orange-500/10 border-orange-500/20'
        case 'tip': return 'text-blue-400 bg-blue-500/10 border-blue-500/20'
        case 'roadworks': return 'text-amber-400 bg-amber-500/10 border-amber-500/20'
        default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20'
      }
    }

    const getHeadingColor = (type: string) => {
      switch (type) {
        case 'hazard': return 'text-red-400'
        case 'condition': return 'text-orange-400'
        case 'tip': return 'text-blue-400'
        case 'roadworks': return 'text-amber-400'
        default: return 'text-gray-400'
      }
    }

    // Calculate validation status
    const confirmationCount = Object.keys(poi.confirmations || {}).length
    const outdatedCount = Object.keys(poi.outdatedVotes || {}).length
    const totalVotes = confirmationCount + outdatedCount
    const confirmationRate = totalVotes > 0 ? confirmationCount / totalVotes : 0
    
    const getValidationStatus = () => {
      if (totalVotes < 3) return null
      if (confirmationRate >= 0.7) return 'confirmed'
      if (confirmationRate <= 0.3) return 'outdated'
      return null
    }

    const validationStatus = getValidationStatus()

    // Calculate distance along route
    const distance = getDistanceAlongRoute(currentRoute, poi.coordinates)

    return (
      <div 
        className={`p-3 rounded-lg border cursor-pointer transition-all hover:bg-white/5 ${
          poi.resolved ? 'opacity-60' : ''
        } ${getPoiColor(poi.type)}`}
        onClick={() => onUserPOIClick?.(poi)}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5 relative">
            {getPoiIcon(poi.type)}
            {validationStatus === 'confirmed' && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-2 h-2 text-white" />
              </div>
            )}
            {validationStatus === 'outdated' && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center">
                <Clock className="w-2 h-2 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`font-semibold text-sm ${getHeadingColor(poi.type)}`}>{poi.name}</span>
              {distance !== null && (
                <span className="text-xs text-gray-400">@ {distance.toFixed(1)}km</span>
              )}
              <span className="text-xs text-gray-500">
                {formatDistanceToNow(poi.createdAt.toDate(), { addSuffix: true })}
              </span>
            </div>
            <p className="text-sm text-gray-200 line-clamp-2 mb-2">{poi.description}</p>
            
            {/* Validation info and actions */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-3">
                {confirmationCount > 0 && (
                  <span className="text-green-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {confirmationCount}
                  </span>
                )}
                {outdatedCount > 0 && (
                  <span className="text-amber-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {outdatedCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" />
                  {poiCommentCounts[poi.id] > 0 ? poiCommentCounts[poi.id] : '0'}
                </span>
                <span className="text-gray-500">by {poi.authorName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Desktop layout (70/30 split)
  if (!isTabletOrSmaller) {
    return (
      <div className="p-3 h-full">
        <div className="flex gap-6">
          {/* Left side - General Discussion (70%) */}
          <div className="flex-[7] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Discussion ({generalComments.length})
              </label>
              {user && (
                <button
                  onClick={() => {
                    setAddModalType('general')
                    setShowAddModal(true)
                  }}
                  className="px-3 py-1 text-xs rounded-lg bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600/30 transition-all flex items-center space-x-1"
                >
                  <Plus className="w-3 h-3" />
                  <span>Add Comment</span>
                </button>
              )}
            </div>
            
            <div 
              className="w-full p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-y-auto"
              style={{ height: '400px' }}
            >
              {loading ? (
                <div className="text-center text-gray-400 py-8">Loading comments...</div>
              ) : generalComments.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No comments yet</p>
                  {user && (
                    <p className="text-sm mt-2">Be the first to start a discussion!</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {generalComments.map(comment => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Route Reports (30%) */}
          <div className="flex-[3] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Route Reports ({(userPOIs?.filter(poi => !poi.resolved).length || 0)})
              </label>
            </div>
            
            <div 
              className="w-full p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-y-auto"
              style={{ height: '400px' }}
            >
              {poisLoading ? (
                <div className="text-center text-gray-400 py-8">Loading reports...</div>
              ) : (!userPOIs || userPOIs.filter(poi => !poi.resolved).length === 0) ? (
                <div className="text-center text-gray-400 py-8">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No route reports yet</p>
                  <p className="text-xs mt-2 text-gray-500">Drag markers from the Overview tab to add reports</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {userPOIs
                    .filter(poi => !poi.resolved)
                    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
                    .map(poi => (
                      <UserPOIItem key={poi.id} poi={poi} />
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        <AddCommentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddComment}
          routeId={routeId}
          type={addModalType}
        />

        <CommentDetailModal
          comment={selectedComment}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedComment(null)
          }}
          onConfirm={user ? confirmCommentAccuracy : undefined}
          onDispute={user ? disputeCommentAccuracy : undefined}
          onHelpful={user ? toggleCommentHelpful : undefined}
          onEdit={user?.id === selectedComment?.authorId ? handleEditComment : undefined}
          onDelete={user?.id === selectedComment?.authorId ? handleDeleteComment : undefined}
          onResolve={user?.id === selectedComment?.authorId ? handleResolveComment : undefined}
          currentUserId={user?.id}
          isAuthor={user?.id === selectedComment?.authorId}
        />
      </div>
    )
  }

  // Tablet/Mobile layout (Accordions)
  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="space-y-3">
        {/* Discussion Accordion */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'discussion' ? null : 'discussion')}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/15 transition-colors flex items-center justify-between backdrop-blur-md"
          >
            <span className="text-sm font-medium text-gray-100">Discussion ({generalComments.length})</span>
            <div className="flex items-center space-x-2">
              {user && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setAddModalType('general')
                    setShowAddModal(true)
                  }}
                  className="p-1 text-gray-400 hover:text-gray-300"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
              {expandedSection === 'discussion' ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>
          
          {expandedSection === 'discussion' && (
            <div className="p-4 bg-transparent">
              {loading ? (
                <div className="text-center text-gray-400 py-4">Loading...</div>
              ) : generalComments.length === 0 ? (
                <div className="text-center text-gray-400 py-4">
                  <p>No comments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {generalComments.map(comment => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Route Reports Accordion */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'reports' ? null : 'reports')}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/15 transition-colors flex items-center justify-between backdrop-blur-md"
          >
            <span className="text-sm font-medium text-gray-100">Route Reports ({(userPOIs?.filter(poi => !poi.resolved).length || 0)})</span>
            <div className="flex items-center space-x-2">
              {expandedSection === 'reports' ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>
          
          {expandedSection === 'reports' && (
            <div className="p-4 bg-transparent">
              {poisLoading ? (
                <div className="text-center text-gray-400 py-4">Loading...</div>
              ) : (!userPOIs || userPOIs.filter(poi => !poi.resolved).length === 0) ? (
                <div className="text-center text-gray-400 py-4">
                  <p className="text-sm">No route reports yet</p>
                  <p className="text-xs mt-2 text-gray-500">Drag markers from the Overview tab to add reports</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {userPOIs
                    .filter(poi => !poi.resolved)
                    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
                    .map(poi => (
                      <UserPOIItem key={poi.id} poi={poi} />
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddCommentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddComment}
        routeId={routeId}
        type={addModalType}
      />

      <CommentDetailModal
        comment={selectedComment}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setSelectedComment(null)
        }}
        onConfirm={user ? confirmCommentAccuracy : undefined}
        onDispute={user ? disputeCommentAccuracy : undefined}
        onHelpful={user ? toggleCommentHelpful : undefined}
        onEdit={user?.id === selectedComment?.authorId ? handleEditComment : undefined}
        onDelete={user?.id === selectedComment?.authorId ? handleDeleteComment : undefined}
        onResolve={user?.id === selectedComment?.authorId ? handleResolveComment : undefined}
        currentUserId={user?.id}
        isAuthor={user?.id === selectedComment?.authorId}
      />
    </div>
  )
}